'use client';
import React, { useState, useEffect } from 'react';
import { MapPin, Clock, ShoppingBasket } from 'lucide-react';
import type { Pantry } from '@/lib/pantryData';

interface PantryListSidebarProps {
  pantries: Pantry[];
  selectedPantry: Pantry | null;
  onSelectPantry: (pantry: Pantry) => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  'aktibo': { label: 'Active', color: 'text-[#16a34a]', bg: 'bg-[#dcfce7]' },
  'mababa-stock': { label: 'Low Stock', color: 'text-[#ca8a04]', bg: 'bg-[#fef9c3]' },
  'walang-stock': { label: 'No Stock', color: 'text-[#dc2626]', bg: 'bg-[#fee2e2]' },
  'hindi-aktibo': { label: 'Closed', color: 'text-[#6b7280]', bg: 'bg-[#f3f4f6]' },
};

export default function PantryListSidebar({ pantries, selectedPantry, onSelectPantry }: PantryListSidebarProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="hidden lg:flex flex-col w-[360px] xl:w-[400px] bg-white border-r border-[#d1ead9] overflow-hidden flex-shrink-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#d1ead9] bg-[#f8fdf9]">
        <p className="text-xs text-[#6b7280]">
          {mounted && (
            <span className="font-semibold text-[#2d7a4f]">{pantries.length}</span>
          )}{mounted ? ' pantries ang available ngayon' : '\u00A0'}
        </p>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {pantries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
            <MapPin size={32} className="text-[#d1ead9] mb-3" />
            <p className="text-sm text-[#9ca3af]">Walang pantry na nahanap.</p>
            <p className="text-xs text-[#c4c4c4] mt-1">Subukan ang ibang filter o search.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#f0faf4]">
            {pantries.map((pantry) => {
              const statusCfg = STATUS_CONFIG[pantry.status] ?? STATUS_CONFIG['hindi-aktibo'];
              const isSelected = selectedPantry?.id === pantry.id;
              return (
                <li key={pantry.id}>
                  <button
                    onClick={() => onSelectPantry(pantry)}
                    className={`w-full text-left px-4 py-4 transition-colors hover:bg-[#f0faf4] focus:outline-none ${
                      isSelected ? 'bg-[#f0faf4] border-l-4 border-[#2d7a4f]' : 'border-l-4 border-transparent'
                    }`}
                  >
                    {/* Name + Status */}
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <h3 className="text-sm font-semibold text-[#1e5c38] leading-snug flex-1">{pantry.pangalan}</h3>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${statusCfg.bg} ${statusCfg.color}`}>
                        {statusCfg.label}
                      </span>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-1.5 mb-1">
                      <MapPin size={12} className="text-[#9ca3af] mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-[#6b7280] leading-snug">{pantry.address}, {pantry.lungsod}</p>
                    </div>

                    {/* Hours */}
                    <div className="flex items-start gap-1.5 mb-1">
                      <Clock size={12} className="text-[#9ca3af] mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-[#6b7280] leading-snug">{pantry.oras}</p>
                    </div>

                    {/* Items */}
                    {pantry.items.length > 0 && (
                      <div className="flex items-start gap-1.5">
                        <ShoppingBasket size={12} className="text-[#9ca3af] mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-[#6b7280] leading-snug line-clamp-1">
                          {pantry.items.join(', ')}
                        </p>
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
